import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Menu,
  X,
  Shield,
  FileText,
  LogOut,
  ChevronDown,
  Headset,
  Home,
  Percent,
  Users,
  GraduationCap,
  Plus,
  BarChart3,
  Settings,
  HelpCircle,
  MessageCircle,
  BookOpen,
  Phone,
  TrendingUp,
  PieChart,
} from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import Images from "../../utils/Images";
import { logoutUser } from "../../utils/authUtils";
import { toast } from "react-toastify";

// Main navigation items
const mainNavItems = [
  { name: "Home", path: "/dashboard", icon: Home },
];

// Dashboard dropdown items
const dashboardItems = [
  { name: "Overview", path: "/dashboard", icon: BarChart3, description: "Main dashboard" },
  { name: "Analytics", path: "/analytics", icon: TrendingUp, description: "Performance metrics" },
  { name: "Reports", path: "/reports", icon: PieChart, description: "Detailed reports" },
];

// Management dropdown items
const managementItems = [
  { name: "Commission", path: "/commission", icon: Percent, description: "Earnings & payments" },
  { name: "Client Registration", path: "/new-registration", icon: Plus, description: "Register new clients" },
  { name: "Others Clients", path: "/others-clients", icon: Users, description: "Non-student clients" },
  { name: "Student Clients", path: "/students-clients", icon: GraduationCap, description: "Student clients" },
];

// Support dropdown items
const supportItems = [
  { name: "Help Center", path: "/help", icon: HelpCircle, description: "FAQs & guides" },
  { name: "Contact Support", path: "/support", icon: MessageCircle, description: "Get help" },
  { name: "Documentation", path: "/docs", icon: BookOpen, description: "User guides" },
  { name: "Live Chat", path: "/chat", icon: Phone, description: "Instant support" },
];

export default function AuthNavbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isDashboardDropdownOpen, setIsDashboardDropdownOpen] = useState(false);
  const [isManagementDropdownOpen, setIsManagementDropdownOpen] = useState(false);
  const [isSupportDropdownOpen, setIsSupportDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const profileButtonRef = useRef(null);
  const dashboardDropdownRef = useRef(null);
  const managementDropdownRef = useRef(null);
  const supportDropdownRef = useRef(null);
  const location = useLocation();
  const { user } = useSelector((state) => state.auth);

  const currentUser = useMemo(() => {
    if (user) {
      return {
        name: `${user.first_name} ${user.last_name}`,
        email: user.email,
        avatarUrl: "",
      };
    }
    return {
      name: "Agent",
      email: "",
      avatarUrl: "",
    };
  }, [user]);

  const userInitials = useMemo(() => {
    const parts = (currentUser?.name || "").split(" ").filter(Boolean);
    if (!parts.length) return "U";
    const first = parts[0]?.[0] || "";
    const last = parts.length > 1 ? parts[parts.length - 1][0] : "";
    return (first + last).toUpperCase();
  }, [currentUser]);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Profile dropdown
      if (
        isDropdownOpen &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        profileButtonRef.current &&
        !profileButtonRef.current.contains(event.target)
      ) {
        setIsDropdownOpen(false);
      }
      
      // Dashboard dropdown
      if (
        isDashboardDropdownOpen &&
        dashboardDropdownRef.current &&
        !dashboardDropdownRef.current.contains(event.target)
      ) {
        setIsDashboardDropdownOpen(false);
      }
      
      // Management dropdown
      if (
        isManagementDropdownOpen &&
        managementDropdownRef.current &&
        !managementDropdownRef.current.contains(event.target)
      ) {
        setIsManagementDropdownOpen(false);
      }
      
      // Support dropdown
      if (
        isSupportDropdownOpen &&
        supportDropdownRef.current &&
        !supportDropdownRef.current.contains(event.target)
      ) {
        setIsSupportDropdownOpen(false);
      }
    };
    
    const handleEsc = (event) => {
      if (event.key === "Escape") {
        setIsDropdownOpen(false);
        setIsDashboardDropdownOpen(false);
        setIsManagementDropdownOpen(false);
        setIsSupportDropdownOpen(false);
      }
    };
    
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEsc);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEsc);
    };
  }, [isDropdownOpen, isDashboardDropdownOpen, isManagementDropdownOpen, isSupportDropdownOpen]);

  const isActiveLink = (path) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen((v) => !v);
    document.body.style.overflow = !isMobileMenuOpen ? "hidden" : "unset";
  };
  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
    document.body.style.overflow = "unset";
  };

  const handleLogout = () => {
    logoutUser();
    toast.success('Logged out successfully');
    window.location.href = '/signin';
  };

  // Helper function to render dropdown items
  const renderDropdownItems = (items, onClose) => {
    return items.map((item, index) => (
      <Link
        key={item.name}
        to={item.path}
        onClick={onClose}
        className={`flex gap-3 items-center p-3 rounded-xl border transition-colors group ${
          index === 0 
            ? 'bg-emerald-50/60 border-emerald-100 hover:bg-emerald-100' 
            : 'bg-emerald-50/60 border-emerald-100 hover:bg-emerald-100'
        }`}
      >
        <span className="flex justify-center items-center w-10 h-10 text-emerald-600 bg-white rounded-xl shadow-sm">
          <item.icon size={20} />
        </span>
        <div>
          <span className="block text-sm font-medium text-emerald-700">
            {item.name}
          </span>
          <span className="text-xs text-emerald-600">
            {item.description}
          </span>
        </div>
      </Link>
    ));
  };

  return (
    <>
      {/* Desktop Navbar */}
      <motion.nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled
            ? "border-b shadow-lg backdrop-blur-md bg-white/95 border-gray-200/50"
            : "backdrop-blur-md bg-black/90"
        }`}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
      >
        <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16 lg:h-20">
            {/* Logo */}
            <motion.div className="flex-shrink-0" whileHover={{ scale: 1.05 }}>
              <Link to="/" className="flex items-center">
                <img
                  src={Images.logo_navbar}
                  alt="TripSecure"
                  className={`h-8 lg:h-10 transition-all duration-300 ${
                    isScrolled
                      ? "filter brightness-0"
                      : "filter brightness-0 invert"
                  }`}
                />
              </Link>
            </motion.div>

            {/* Desktop Navigation Links */}
            <div className="hidden lg:flex lg:items-center lg:space-x-6">
              {/* Home Link */}
              {mainNavItems.map((link, index) => (
                <motion.div
                  key={link.name}
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.08, duration: 0.4 }}
                >
                  <Link
                    to={link.path}
                    className={`relative px-3 py-2 text-sm font-medium transition-colors ${
                      isScrolled
                        ? isActiveLink(link.path)
                          ? "text-emerald-600"
                          : "text-gray-700 hover:text-emerald-600"
                        : isActiveLink(link.path)
                        ? "text-emerald-400"
                        : "text-white hover:text-emerald-400"
                    }`}
                  >
                    {link.name}
                    {isActiveLink(link.path) && (
                      <motion.div
                        className="absolute bottom-0 left-0 right-0 h-0.5 bg-emerald-500"
                        layoutId="activeTabAgentAuth"
                        initial={false}
                        transition={{
                          type: "spring",
                          stiffness: 300,
                          damping: 30,
                        }}
                      />
                    )}
                  </Link>
                </motion.div>
              ))}

              {/* Dashboard Dropdown */}
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.16, duration: 0.4 }}
                className="relative"
              >
                <motion.button
                  ref={dashboardDropdownRef}
                  onClick={() => setIsDashboardDropdownOpen(!isDashboardDropdownOpen)}
                  className={`group flex items-center gap-2 px-3 py-2 text-sm font-medium transition-colors ${
                    isScrolled
                      ? "text-gray-700 hover:text-emerald-600"
                      : "text-white hover:text-emerald-400"
                  }`}
                  whileHover={{ y: -1 }}
                >
                  <BarChart3 size={16} />
                  Dashboard
                  <ChevronDown
                    className={`transition-transform duration-300 ${
                      isDashboardDropdownOpen ? "rotate-180" : ""}`}
                    size={14}
                  />
                </motion.button>

                <AnimatePresence>
                  {isDashboardDropdownOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: -8, scale: 0.98 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -8, scale: 0.98 }}
                      transition={{
                        type: "spring",
                        stiffness: 300,
                        damping: 24,
                      }}
                      className="absolute right-0 top-[calc(100%+10px)] w-64 bg-white/90 backdrop-blur-xl border border-emerald-100 shadow-2xl rounded-2xl overflow-hidden"
                    >
                      <div className="w-full h-1 bg-gradient-to-r from-emerald-500 via-green-500 to-emerald-500" />
                      <div className="p-2 space-y-2">
                        {renderDropdownItems(dashboardItems, () => setIsDashboardDropdownOpen(false))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>

              {/* Management Dropdown */}
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.24, duration: 0.4 }}
                className="relative"
              >
                <motion.button
                  ref={managementDropdownRef}
                  onClick={() => setIsManagementDropdownOpen(!isManagementDropdownOpen)}
                  className={`group flex items-center gap-2 px-3 py-2 text-sm font-medium transition-colors ${
                    isScrolled
                      ? "text-gray-700 hover:text-emerald-600"
                      : "text-white hover:text-emerald-400"
                  }`}
                  whileHover={{ y: -1 }}
                >
                  <Users size={16} />
                  Management
                  <ChevronDown
                    className={`transition-transform duration-300 ${
                      isManagementDropdownOpen ? "rotate-180" : ""}`}
                    size={14}
                  />
                </motion.button>

                <AnimatePresence>
                  {isManagementDropdownOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: -8, scale: 0.98 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -8, scale: 0.98 }}
                      transition={{
                        type: "spring",
                        stiffness: 300,
                        damping: 24,
                      }}
                      className="absolute right-0 top-[calc(100%+10px)] w-72 bg-white/90 backdrop-blur-xl border border-emerald-100 shadow-2xl rounded-2xl overflow-hidden"
                    >
                      <div className="w-full h-1 bg-gradient-to-r from-emerald-500 via-green-500 to-emerald-500" />
                      <div className="p-2 space-y-2">
                        {renderDropdownItems(managementItems, () => setIsManagementDropdownOpen(false))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>

              {/* Support Dropdown */}
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.32, duration: 0.4 }}
                className="relative"
              >
                <motion.button
                  ref={supportDropdownRef}
                  onClick={() => setIsSupportDropdownOpen(!isSupportDropdownOpen)}
                  className={`group flex items-center gap-2 px-3 py-2 text-sm font-medium transition-colors ${
                    isScrolled
                      ? "text-gray-700 hover:text-emerald-600"
                      : "text-white hover:text-emerald-400"
                  }`}
                  whileHover={{ y: -1 }}
                >
                  <Headset size={16} />
                  Support
                  <ChevronDown
                    className={`transition-transform duration-300 ${
                      isSupportDropdownOpen ? "rotate-180" : ""}`}
                    size={14}
                  />
                </motion.button>

                <AnimatePresence>
                  {isSupportDropdownOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: -8, scale: 0.98 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -8, scale: 0.98 }}
                      transition={{
                        type: "spring",
                        stiffness: 300,
                        damping: 24,
                      }}
                      className="absolute right-0 top-[calc(100%+10px)] w-64 bg-white/90 backdrop-blur-xl border border-emerald-100 shadow-2xl rounded-2xl overflow-hidden"
                    >
                      <div className="w-full h-1 bg-gradient-to-r from-emerald-500 via-green-500 to-emerald-500" />
                      <div className="p-2 space-y-2">
                        {renderDropdownItems(supportItems, () => setIsSupportDropdownOpen(false))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>

            </div>

            {/* User Menu */}
            <div className="hidden relative lg:flex lg:items-center lg:space-x-4">
              <motion.button
                ref={profileButtonRef}
                onClick={() => setIsDropdownOpen((v) => !v)}
                className={`group flex items-center gap-3 px-2 py-1.5 rounded-full transition-all duration-300 ${
                  isScrolled
                    ? "hover:bg-gray-100"
                    : "hover:bg-white/10 text-white"
                }`}
                whileHover={{ y: -1, scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <span className="inline-flex relative">
                  {currentUser?.avatarUrl ? (
                    <img
                      src={currentUser.avatarUrl}
                      alt={currentUser.name}
                      className="object-cover w-9 h-9 rounded-full ring-2 ring-emerald-500/40"
                    />
                  ) : (
                    <div className="grid place-items-center w-9 h-9 text-sm font-semibold text-white bg-gradient-to-br from-emerald-500 to-green-600 rounded-full ring-2 ring-emerald-500/40">
                      {userInitials}
                    </div>
                  )}
                </span>
                <div className="hidden text-left xl:block">
                  <p
                    className={`${
                      isScrolled ? "text-gray-800" : "text-white"
                    } text-sm font-semibold leading-5`}
                  >
                    {currentUser?.name || "Agent"}
                  </p>
                  <p className="text-xs text-gray-500 truncate max-w-[160px]">
                    {currentUser?.email || ""}
                  </p>
                </div>
                <ChevronDown
                  className={`transition-transform duration-300 ${
                    isDropdownOpen ? "rotate-180" : ""
                  } ${isScrolled ? "text-gray-600" : "text-white"}`}
                  size={18}
                />
              </motion.button>

              <AnimatePresence>
                {isDropdownOpen && (
                  <motion.div
                    ref={dropdownRef}
                    initial={{ opacity: 0, y: -8, scale: 0.98 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -8, scale: 0.98 }}
                    transition={{ type: "spring", stiffness: 300, damping: 24 }}
                    className="absolute right-0 top-[calc(100%+10px)] w-80 bg-white/90 backdrop-blur-xl border border-emerald-100 shadow-2xl rounded-2xl overflow-hidden"
                  >
                    <div className="w-full h-1 bg-gradient-to-r from-emerald-500 via-green-500 to-emerald-500" />
                    <div className="flex gap-3 items-center p-4">
                      <div className="relative">
                        {currentUser?.avatarUrl ? (
                          <img
                            src={currentUser.avatarUrl}
                            alt={currentUser.name}
                            className="object-cover w-12 h-12 rounded-full ring-2 ring-emerald-500/40"
                          />
                        ) : (
                          <div className="grid place-items-center w-12 h-12 text-base font-semibold text-white bg-gradient-to-br from-emerald-500 to-green-600 rounded-full ring-2 ring-emerald-500/40">
                            {userInitials}
                          </div>
                        )}
                      </div>
                      <div className="min-w-0">
                        <p className="font-semibold leading-5 text-gray-900 truncate">
                          {currentUser?.name}
                        </p>
                        <p className="text-xs text-gray-500 truncate">
                          {currentUser?.email}
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-2 px-2 pb-2">
                      <Link
                        to="/my-policies"
                        className="flex flex-col justify-center items-center p-3 rounded-xl border border-emerald-100 transition-colors group bg-emerald-50/60 hover:bg-emerald-50"
                      >
                        <span className="flex justify-center items-center w-10 h-10 text-emerald-600 bg-white rounded-xl shadow-sm">
                          <Shield size={20} />
                        </span>
                        <span className="mt-1.5 text-xs font-medium text-emerald-700">
                          Policies
                        </span>
                      </Link>
                      <Link
                        to="/claim/list"
                        className="flex flex-col justify-center items-center p-3 rounded-xl border border-emerald-100 transition-colors group bg-emerald-50/60 hover:bg-emerald-50"
                      >
                        <span className="flex justify-center items-center w-10 h-10 text-emerald-600 bg-white rounded-xl shadow-sm">
                          <FileText size={20} />
                        </span>
                        <span className="mt-1.5 text-xs font-medium text-emerald-700">
                          Claims
                        </span>
                      </Link>
                      <Link
                        to="/settings"
                        className="flex flex-col justify-center items-center p-3 rounded-xl border border-emerald-100 transition-colors group bg-emerald-50/60 hover:bg-emerald-50"
                      >
                        <span className="flex justify-center items-center w-10 h-10 text-emerald-600 bg-white rounded-xl shadow-sm">
                          <Settings size={20} />
                        </span>
                        <span className="mt-1.5 text-xs font-medium text-emerald-700">
                          Settings
                        </span>
                      </Link>
                    </div>

                    <div className="px-3 pb-3">
                      <div className="w-full h-px bg-gradient-to-r from-transparent via-emerald-100 to-transparent" />
                    </div>

                    <div className="px-2 pb-2">
                      <button
                        onClick={handleLogout}
                        className="w-full flex items-center justify-between px-3 py-2.5 rounded-xl bg-gradient-to-r from-red-50 to-white hover:from-red-100 hover:to-white text-red-600 border border-red-100 transition-colors"
                      >
                        <span className="flex gap-2 items-center">
                          <span className="flex justify-center items-center w-8 h-8 text-red-600 bg-red-100 rounded-lg">
                            <LogOut size={18} />
                          </span>
                          <span className="text-sm font-semibold">Logout</span>
                        </span>
                        <span className="text-xs text-red-500/80">
                          Securely sign out
                        </span>
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Mobile menu button */}
            <div className="lg:hidden">
              <motion.button
                onClick={toggleMobileMenu}
                className={`p-2 rounded-md transition-all duration-300 ${
                  isScrolled
                    ? "text-gray-700 hover:text-emerald-600 hover:bg-gray-100"
                    : "text-white hover:text-emerald-400 hover:bg-white/10"
                }`}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <Menu size={24} />
              </motion.button>
            </div>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            <motion.div
              className="fixed inset-0 z-40 backdrop-blur-sm bg-black/50 lg:hidden"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              onClick={closeMobileMenu}
            />
            <motion.div
              className="fixed top-0 right-0 h-full w-80 max-w-[85vw] bg-white shadow-2xl z-50 lg:hidden"
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
            >
              <div className="flex overflow-hidden flex-col h-full">
                <div className="flex justify-between items-center p-6 border-b border-gray-200">
                  <img
                    src={Images.logo_navbar}
                    alt="TripSecure"
                    className="h-8 filter brightness-0"
                  />
                  <motion.button
                    onClick={closeMobileMenu}
                    className="p-2 text-gray-500 rounded-md hover:text-gray-700 hover:bg-gray-100"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <X size={24} />
                  </motion.button>
                </div>
                <div className="p-6 border-b border-gray-200">
                  <div className="flex gap-3 items-center">
                    {currentUser?.avatarUrl ? (
                      <img
                        src={currentUser.avatarUrl}
                        alt={currentUser.name}
                        className="object-cover w-12 h-12 rounded-full ring-2 ring-emerald-500/40"
                      />
                    ) : (
                      <div className="grid place-items-center w-12 h-12 text-base font-semibold text-white bg-gradient-to-br from-emerald-500 to-green-600 rounded-full ring-2 ring-emerald-500/40">
                        {userInitials}
                      </div>
                    )}
                    <div className="min-w-0">
                      <p className="font-semibold leading-5 text-gray-900 truncate">
                        {currentUser?.name}
                      </p>
                      <p className="text-xs text-gray-500 truncate">
                        {currentUser?.email}
                      </p>
                    </div>
                  </div>
                </div>
                <nav className="overflow-y-auto flex-1 px-6 py-6 space-y-2">
                  {/* Main Navigation */}
                  {mainNavItems.map((link, index) => (
                    <motion.div
                      key={link.name}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.1 + index * 0.08, duration: 0.25 }}
                    >
                      <Link
                        to={link.path}
                        onClick={closeMobileMenu}
                        className={`block px-4 py-3 rounded-lg font-medium transition-all duration-300 ${
                          isActiveLink(link.path)
                            ? "bg-emerald-50 text-emerald-700 border-l-4 border-emerald-500"
                            : "text-gray-700 hover:bg-gray-50 hover:text-emerald-600"
                        }`}
                      >
                        {link.name}
                      </Link>
                    </motion.div>
                  ))}

                  {/* Dashboard Section */}
                  <div className="pt-4 border-t border-gray-200">
                    <h3 className="px-4 py-2 text-xs font-semibold tracking-wider text-gray-500 uppercase">
                      Dashboard
                    </h3>
                    {dashboardItems.map((item, index) => (
                      <motion.div
                        key={item.name}
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.2 + index * 0.08, duration: 0.25 }}
                      >
                        <Link
                          to={item.path}
                          onClick={closeMobileMenu}
                          className={`flex gap-3 items-center px-4 py-3 font-medium rounded-lg transition-all duration-300 ${
                            isActiveLink(item.path)
                              ? "bg-emerald-50 text-emerald-700 border-l-4 border-emerald-500"
                              : "text-gray-700 hover:bg-emerald-50 hover:text-emerald-600"
                          }`}
                        >
                          <item.icon size={18} className="text-emerald-600" />
                          <div>
                            <div className="text-sm font-medium">{item.name}</div>
                            <div className="text-xs text-gray-500">{item.description}</div>
                          </div>
                        </Link>
                      </motion.div>
                    ))}
                  </div>

                  {/* Management Section */}
                  <div className="pt-4 border-t border-gray-200">
                    <h3 className="px-4 py-2 text-xs font-semibold tracking-wider text-gray-500 uppercase">
                      Management
                    </h3>
                    {managementItems.map((item, index) => (
                      <motion.div
                        key={item.name}
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.3 + index * 0.08, duration: 0.25 }}
                      >
                        <Link
                          to={item.path}
                          onClick={closeMobileMenu}
                          className={`flex gap-3 items-center px-4 py-3 font-medium rounded-lg transition-all duration-300 ${
                            isActiveLink(item.path)
                              ? "bg-emerald-50 text-emerald-700 border-l-4 border-emerald-500"
                              : "text-gray-700 hover:bg-emerald-50 hover:text-emerald-600"
                          }`}
                        >
                          <item.icon size={18} className="text-emerald-600" />
                          <div>
                            <div className="text-sm font-medium">{item.name}</div>
                            <div className="text-xs text-gray-500">{item.description}</div>
                          </div>
                        </Link>
                      </motion.div>
                    ))}
                  </div>

                  {/* Support Section */}
                  <div className="pt-4 border-t border-gray-200">
                    <h3 className="px-4 py-2 text-xs font-semibold tracking-wider text-gray-500 uppercase">
                      Support
                    </h3>
                    {supportItems.map((item, index) => (
                      <motion.div
                        key={item.name}
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.4 + index * 0.08, duration: 0.25 }}
                      >
                        <Link
                          to={item.path}
                          onClick={closeMobileMenu}
                          className={`flex gap-3 items-center px-4 py-3 font-medium rounded-lg transition-all duration-300 ${
                            isActiveLink(item.path)
                              ? "bg-emerald-50 text-emerald-700 border-l-4 border-emerald-500"
                              : "text-gray-700 hover:bg-emerald-50 hover:text-emerald-600"
                          }`}
                        >
                          <item.icon size={18} className="text-emerald-600" />
                          <div>
                            <div className="text-sm font-medium">{item.name}</div>
                            <div className="text-xs text-gray-500">{item.description}</div>
                          </div>
                        </Link>
                      </motion.div>
                    ))}
                  </div>

                  <div className="grid grid-cols-3 gap-3 pt-2">
                    <Link
                      to="/my-policies"
                      onClick={closeMobileMenu}
                      className="flex flex-col justify-center items-center p-3 rounded-xl border border-emerald-100 bg-emerald-50/60 hover:bg-emerald-50"
                    >
                      <span className="flex justify-center items-center w-10 h-10 text-emerald-600 bg-white rounded-xl shadow-sm">
                        <Shield size={20} />
                      </span>
                      <span className="mt-1.5 text-xs font-medium text-emerald-700">
                        Policies
                      </span>
                    </Link>
                    <Link
                      to="/claim/list"
                      onClick={closeMobileMenu}
                      className="flex flex-col justify-center items-center p-3 rounded-xl border border-emerald-100 bg-emerald-50/60 hover:bg-emerald-50"
                    >
                      <span className="flex justify-center items-center w-10 h-10 text-emerald-600 bg-white rounded-xl shadow-sm">
                        <FileText size={20} />
                      </span>
                      <span className="mt-1.5 text-xs font-medium text-emerald-700">
                        Claims
                      </span>
                    </Link>
                    <Link
                      to="/settings"
                      onClick={closeMobileMenu}
                      className="flex flex-col justify-center items-center p-3 rounded-xl border border-emerald-100 bg-emerald-50/60 hover:bg-emerald-50"
                    >
                      <span className="flex justify-center items-center w-10 h-10 text-emerald-600 bg-white rounded-xl shadow-sm">
                        <Settings size={20} />
                      </span>
                      <span className="mt-1.5 text-xs font-medium text-emerald-700">
                        Settings
                      </span>
                    </Link>
                  </div>
                </nav>
                <div className="p-6 border-t border-gray-200">
                  <motion.button
                    className="flex gap-2 justify-center items-center px-6 py-3 w-full font-medium text-red-600 bg-red-50 rounded-lg border border-red-100 shadow-sm transition-all duration-300 hover:bg-red-100"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleLogout}
                  >
                    <LogOut size={18} /> Logout
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
